/**
 * Unified attachment type system for user messages.
 * Supports images, documents, URLs, and integration content.
 */

import type { ImageAttachment, ProcessedImageAttachment, SupportedImageMimeType } from './image-attachment';

// ========================================
// ATTACHMENT KINDS
// ========================================

export type AttachmentKind = 'image' | 'document' | 'url' | 'integration';

// ========================================
// DOCUMENT TYPES
// ========================================

export const SUPPORTED_DOCUMENT_MIME_TYPES = [
	'application/pdf',
	'text/plain',
	'text/markdown',
	'text/csv',
	'text/html',
	'application/json',
] as const;

export type SupportedDocumentMimeType = typeof SUPPORTED_DOCUMENT_MIME_TYPES[number];

export function isSupportedDocumentType(mimeType: string): mimeType is SupportedDocumentMimeType {
	return SUPPORTED_DOCUMENT_MIME_TYPES.includes(mimeType as SupportedDocumentMimeType);
}

export function getDocumentFileExtension(mimeType: SupportedDocumentMimeType): string {
	const map: Record<SupportedDocumentMimeType, string> = {
		'application/pdf': 'pdf',
		'text/plain': 'txt',
		'text/markdown': 'md',
		'text/csv': 'csv',
		'text/html': 'html',
		'application/json': 'json',
	};
	return map[mimeType] || 'bin';
}

// ========================================
// CONTEXT INTEGRATION PROVIDER
// ========================================

export type ContextIntegrationProvider = 'notion' | 'linear' | 'google_drive';

// ========================================
// CLIENT-SIDE ATTACHMENT DATA
// ========================================

/**
 * Image attachment data (preserves existing ImageAttachment shape with kind discriminator)
 */
export interface ImageAttachmentData {
	kind: 'image';
	id: string;
	filename: string;
	mimeType: SupportedImageMimeType;
	base64Data: string;
	size?: number;
	dimensions?: {
		width: number;
		height: number;
	};
}

/**
 * Document attachment for PDFs, text files, markdown, etc.
 */
export interface DocumentAttachmentData {
	kind: 'document';
	id: string;
	filename: string;
	mimeType: SupportedDocumentMimeType;
	base64Data: string;
	size?: number;
	/** Client-side extracted text for txt/md/csv/json; server-side for PDF */
	extractedText?: string;
}

/**
 * URL attachment for web content
 */
export interface UrlAttachmentData {
	kind: 'url';
	id: string;
	url: string;
	title?: string;
	/** Populated server-side via fetch */
	extractedText?: string;
}

/**
 * Integration content attachment (Notion pages, Linear issues, etc.)
 */
export interface IntegrationAttachmentData {
	kind: 'integration';
	id: string;
	provider: ContextIntegrationProvider;
	contentId: string;
	title: string;
	url?: string;
	extractedText: string;
}

/**
 * Discriminated union of all attachment types
 */
export type Attachment = ImageAttachmentData | DocumentAttachmentData | UrlAttachmentData | IntegrationAttachmentData;

// ========================================
// PROCESSED (SERVER-SIDE) ATTACHMENTS
// ========================================

export interface ProcessedDocumentAttachment {
	kind: 'document';
	mimeType: SupportedDocumentMimeType;
	r2Key: string;
	hash: string;
	extractedText: string;
	originalFilename: string;
	size: number;
}

export interface ProcessedUrlAttachment {
	kind: 'url';
	url: string;
	title: string;
	extractedText: string;
	fetchedAt: string;
}

export interface ProcessedIntegrationAttachment {
	kind: 'integration';
	provider: ContextIntegrationProvider;
	contentId: string;
	title: string;
	url?: string;
	extractedText: string;
}

/**
 * Processed image wraps existing ProcessedImageAttachment with kind discriminator
 */
export type ProcessedImageAttachmentWithKind = ProcessedImageAttachment & { kind: 'image' };

/**
 * Union of all processed attachment types
 */
export type ProcessedAttachment =
	| ProcessedImageAttachmentWithKind
	| ProcessedDocumentAttachment
	| ProcessedUrlAttachment
	| ProcessedIntegrationAttachment;

// ========================================
// CONSTANTS
// ========================================

/** Maximum document file size (25MB) */
export const MAX_DOCUMENT_SIZE_BYTES = 25 * 1024 * 1024;

/** Maximum total attachments per message (images + documents + URLs + integrations) */
export const MAX_ATTACHMENTS_PER_MESSAGE = 5;

/** Maximum extracted text length in characters */
export const MAX_EXTRACTED_TEXT_LENGTH = 50_000;

// ========================================
// NORMALIZATION UTILITIES
// ========================================

/**
 * Normalize a legacy ImageAttachment (no `kind` field) into an ImageAttachmentData.
 * Handles backward compatibility with existing image-only flow.
 */
export function normalizeImageToAttachment(image: ImageAttachment): ImageAttachmentData {
	return {
		kind: 'image',
		id: image.id,
		filename: image.filename,
		mimeType: image.mimeType,
		base64Data: image.base64Data,
		size: image.size,
		dimensions: image.dimensions,
	};
}

/**
 * Normalize an attachment that may or may not have a `kind` field.
 * Legacy images sent without `kind` are auto-detected and normalized.
 */
export function normalizeAttachment(input: Attachment | ImageAttachment): Attachment {
	if ('kind' in input && input.kind) {
		return input as Attachment;
	}
	// Legacy ImageAttachment without kind
	return normalizeImageToAttachment(input as ImageAttachment);
}

/**
 * Extract text content from processed attachments for LLM context injection.
 * Returns formatted text blocks suitable for inclusion in prompts.
 */
export function formatAttachmentsForContext(attachments: ProcessedAttachment[]): string {
	const textBlocks: string[] = [];

	for (const attachment of attachments) {
		switch (attachment.kind) {
			case 'document':
				textBlocks.push(`## Document: ${attachment.originalFilename}\n${attachment.extractedText}`);
				break;
			case 'url':
				textBlocks.push(`## URL: ${attachment.title} (${attachment.url})\n${attachment.extractedText}`);
				break;
			case 'integration':
				textBlocks.push(`## ${providerLabel(attachment.provider)}: ${attachment.title}\n${attachment.extractedText}`);
				break;
			// Images are handled via multi-modal content, not text injection
			case 'image':
				break;
		}
	}

	if (textBlocks.length === 0) return '';
	return `<attached_documents>\n${textBlocks.join('\n\n')}\n</attached_documents>`;
}

function providerLabel(provider: ContextIntegrationProvider): string {
	const labels: Record<ContextIntegrationProvider, string> = {
		notion: 'Notion Page',
		linear: 'Linear',
		google_drive: 'Google Drive',
	};
	return labels[provider] || provider;
}

// ========================================
// CONTENT BROWSING TYPES (Phase 3B)
// ========================================

export interface ContentItem {
	id: string;
	title: string;
	type: string;
	url?: string;
	updatedAt?: string;
	preview?: string;
}

export interface ContentDetail {
	id: string;
	title: string;
	type: string;
	url: string;
	extractedText: string;
	provider: string;
}

export interface ContextIntegrationInfo {
	provider: ContextIntegrationProvider;
	workspaceName: string | null;
	connectedAt: string;
	connectedBy: string | null;
}
